import { useCallback, useEffect, useRef, useState } from "react";

/*
  Shared behaviour for the modals in this folder.

  useModalLayer(open, { onEscape, containerRef, lockScroll })
    - Escape is handled only by the top-most open modal, so pressing it on a
      confirmation that sits above another modal closes just the confirmation.
    - Locks page scrolling while any modal is open (counted, so nested modals
      restore it correctly) — the modal, not the page, is the scroll target.
    - Moves focus into the dialog when it opens (unless something inside was
      auto-focused) and returns it to the opener when it closes.

  useSubmitGuard()
    - [busy, run]: run(action, ...args) calls the action; if it returns a
      promise, further calls are ignored until it settles. Protects every
      caller against double submissions without changing their handlers.
*/

const openLayers = [];
let scrollLockCount = 0;
let savedBodyOverflow = "";

export function useModalLayer(open, { onEscape, containerRef, lockScroll = true } = {}) {
  const onEscapeRef = useRef(onEscape);
  const wasOpenRef = useRef(false);
  const openerRef = useRef(null);
  const initialFocusRef = useRef(null);

  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  // Captured while rendering the opening frame: by the time effects run, an
  // autoFocus inside the dialog has already taken focus.
  if (open && !wasOpenRef.current && typeof document !== "undefined") {
    openerRef.current = document.activeElement;
    initialFocusRef.current = null;
  }
  wasOpenRef.current = open;

  useEffect(() => {
    if (!open) return undefined;

    const layer = {};
    openLayers.push(layer);

    if (lockScroll) {
      if (scrollLockCount === 0) {
        savedBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
      }
      scrollLockCount += 1;
    }

    // Remember what was auto-focused inside, so a re-run of this effect (e.g.
    // StrictMode's mount/unmount/mount) restores it instead of the container.
    const container = containerRef?.current;
    if (container?.contains(document.activeElement)) {
      initialFocusRef.current = document.activeElement;
    } else if (container) {
      const initial = initialFocusRef.current;
      const target = initial && container.contains(initial) ? initial : container;
      target.focus({ preventScroll: true });
    }

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (openLayers[openLayers.length - 1] !== layer) return;
      onEscapeRef.current?.(event);
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const index = openLayers.indexOf(layer);
      if (index >= 0) openLayers.splice(index, 1);

      if (lockScroll) {
        scrollLockCount = Math.max(0, scrollLockCount - 1);
        if (scrollLockCount === 0) document.body.style.overflow = savedBodyOverflow;
      }

      const opener = openerRef.current;
      if (opener && opener !== document.body && document.contains(opener)) {
        opener.focus?.({ preventScroll: true });
      }
    };
    // containerRef is a stable ref object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lockScroll]);
}

export function useSubmitGuard() {
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async (action, ...args) => {
    if (busyRef.current || typeof action !== "function") return undefined;

    const result = action(...args);
    if (!result || typeof result.then !== "function") return result;

    busyRef.current = true;
    setBusy(true);
    try {
      return await result;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  return [busy, run];
}
